/**
 * LotsPage — the QC-lot worklist. Enrol a brouillon lot, then VALIDATE it to freeze cible/ecart_type
 * (médico-légal: a re-cal is a new lot). Lists every lot with its statut chip (color + glyph + text) and
 * an expiry sweep. Drives the full 5-state DoD + the module-absent (cloud/unmounted) degradation panel.
 *
 * Data: GET /qc/lots (cap qc.lot.manage). Mutations: POST /qc/lots, /qc/lots/:id/valider, /qc/lots/sweep.
 */
import { useState } from "react";
import {
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { QC_CAPS } from "../caps";
import { useLabId, useLots } from "../api";
import type { LotControle } from "../types";
import { idStr } from "../types";
import { fmtNum, fmtTime, isModuleAbsent } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { LotStatutBadge } from "../components/StatusBadge";
import { ModuleAbsentState } from "../components/ModuleAbsentState";
import { EnrolLotModal } from "../components/EnrolLotModal";
import { ValiderLotModal } from "../components/ValiderLotModal";
import { SweepModal } from "../components/SweepModal";

export function LotsPage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canManage = useCan(QC_CAPS.lotManage);

  const lotsQuery = useLots(labId, canManage && online);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [sweepOpen, setSweepOpen] = useState(false);
  const [validerLot, setValiderLot] = useState<LotControle | null>(null);

  const columns: ReadonlyArray<Column<LotControle>> = [
    { key: "code", header: t("qc.lots.col.code"), mono: true, sortable: true, sortAccessor: (r) => r.code, cell: (r) => r.code },
    { key: "parametre", header: t("qc.lots.col.parametre"), mono: true, sortable: true, sortAccessor: (r) => r.parametre_code, cell: (r) => r.parametre_code },
    { key: "niveau", header: t("qc.lots.col.niveau"), cell: (r) => t(`qc.niveau.${r.niveau}`) },
    {
      key: "cible",
      header: t("qc.lots.col.cible"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{fmtNum(r.cible)}</span>,
    },
    {
      key: "sd",
      header: t("qc.lots.col.sd"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{fmtNum(r.ecart_type)}</span>,
    },
    {
      key: "peremption",
      header: t("qc.lots.col.peremption"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.date_peremption,
      cell: (r) => <span className="nv-ltr-island">{fmtTime(r.date_peremption)}</span>,
    },
    { key: "statut", header: t("qc.lots.col.statut"), cell: (r) => <LotStatutBadge statut={r.statut} /> },
    {
      key: "actions",
      header: t("qc.lots.col.actions"),
      align: "end",
      cell: (r) =>
        r.statut === "brouillon" ? (
          <Button size="sm" variant="secondary" disabled={!canManage} onClick={() => setValiderLot(r)}>
            {t("qc.lots.action.valider")}
          </Button>
        ) : null,
    },
  ];

  const actions = (
    <>
      <Button variant="secondary" disabled={!canManage} onClick={() => setSweepOpen(true)}>
        {t("qc.lots.sweep")}
      </Button>
      <Button disabled={!canManage} onClick={() => setEnrolOpen(true)}>
        {t("qc.lots.new")}
      </Button>
    </>
  );

  const header = <PageHeader title={t("qc.lots.title")} subtitle={t("qc.lots.subtitle")} actions={actions} />;

  if (!canManage) return <ForbiddenState requiredCapabilities={[QC_CAPS.lotManage]} />;

  let body: React.ReactNode;
  if (!online) {
    body = <OfflineState onRetry={() => void lotsQuery.refetch()} cached={(lotsQuery.data?.length ?? 0) > 0} />;
  } else if (lotsQuery.isLoading) {
    body = <LoadingState variant="table" rows={6} />;
  } else if (lotsQuery.isError && isModuleAbsent(lotsQuery.error)) {
    body = <ModuleAbsentState />;
  } else if (lotsQuery.isError) {
    body = (
      <ErrorState
        message={lotsQuery.error.message}
        code={lotsQuery.error.code}
        onRetry={() => void lotsQuery.refetch()}
      />
    );
  } else {
    body = (
      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={lotsQuery.data ?? []}
            rowKey={(r) => idStr(r.id)}
            caption={t("qc.lots.title")}
            emptyTitle={t("qc.lots.empty.title")}
            emptyDescription={t("qc.lots.empty.desc")}
            pageSize={15}
          />
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}
      {body}

      <EnrolLotModal open={enrolOpen} onClose={() => setEnrolOpen(false)} onEnrolled={() => setEnrolOpen(false)} />
      <ValiderLotModal
        open={validerLot !== null}
        lot={validerLot}
        onClose={() => setValiderLot(null)}
        onValidated={() => setValiderLot(null)}
      />
      <SweepModal open={sweepOpen} onClose={() => setSweepOpen(false)} onSwept={() => setSweepOpen(false)} />
    </div>
  );
}
